/**
 * DiscussMessageThread — message-thread renderer for the Discuss modal.
 *
 * Mirrors the role of `features/session/components/MessageThread.tsx` but
 * reads from `useDiscussStore` (keyed by executionId) instead of the
 * singleton `useSessionStore`. Visual primitives (`MessageBubble`,
 * `TypingIndicator`) are reused directly because they're prop-driven
 * dumb components.
 *
 * Auto-scroll: pinned to the bottom on every new turn so a multi-turn
 * debugging conversation behaves like any other chat thread. We skip
 * the scroll if the user has manually scrolled up — same heuristic as
 * the singleton `MessageThread`, but kept simple here (within 80px of
 * the bottom is "near enough" to keep auto-scrolling).
 */

import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

import { MessageBubble } from '@/features/session/components/MessageBubble'
import { TypingIndicator } from '@/features/session/components/TypingIndicator'
import {
  selectMessages,
  selectStreamingState,
  useDiscussStore,
  type AgentType,
} from '@/features/queue/stores/discuss-store'

export interface DiscussMessageThreadProps {
  executionId: string
  /**
   * Which agent's transcript to render. The store is keyed by
   * `(executionId, agentType)` (Task 3701h G.1) so the thread must
   * match the agent the parent modal is showing in its dropdown,
   * otherwise the operator sees the other agent's conversation.
   */
  agentType: AgentType
  /** Status string shown when there are no messages yet (e.g.
   *  "Loading context..." or "Ready — ask anything"). */
  emptyHint?: string
}

const AUTOSCROLL_NEAR_BOTTOM_PX = 80

export function DiscussMessageThread({
  executionId,
  agentType,
  emptyHint,
}: DiscussMessageThreadProps) {
  const messages = useDiscussStore((s) =>
    selectMessages(s, executionId, agentType),
  )
  const streamingState = useDiscussStore((s) =>
    selectStreamingState(s, executionId, agentType),
  )

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const endRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to bottom when a new turn lands or when streaming tokens
  // arrive, unless the operator has scrolled up to read history.
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight
    if (distanceFromBottom <= AUTOSCROLL_NEAR_BOTTOM_PX) {
      endRef.current?.scrollIntoView({ block: 'end' })
    }
  }, [messages, streamingState])

  const showTypingIndicator =
    streamingState === 'streaming' &&
    messages.length > 0 &&
    !messages[messages.length - 1]?.content

  return (
    <div ref={scrollContainerRef} style={styles.container} data-testid="discuss-message-thread">
      {messages.length === 0 && (
        <div style={styles.empty}>
          <p style={styles.emptyText}>{emptyHint ?? 'Ask a question to begin.'}</p>
        </div>
      )}
      <div style={styles.messages}>
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {showTypingIndicator && <TypingIndicator />}
      </div>
      <div ref={endRef} aria-hidden="true" />
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '16px 20px',
    background: 'var(--bg-primary)',
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    textAlign: 'center',
  },
  emptyText: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
}

export default DiscussMessageThread
